/**
 * Simple Column Labels Component
 * Props-based, no store dependency
 */

import React, { memo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { GripVertical, Plus } from 'lucide-react';
import { useSelector } from '@xstate/store/react';
import { popupStore } from '../../stores/popupStore';
import { getMaxRow } from '../../utils/boardHelpers';
import { ColumnEditPopover } from './ColumnEditPopover';
import { useColumnYOffsets, useRowAssignments, useRowHeight, useLayoutTotalHeight, useSidebarWidth } from '../BoardLayoutContext';
import { useColumnNames, useColumns as useColumnDefs } from '../../../stores/hooks';

// ============================================
// Types
// ============================================

interface ColumnLabelsSimpleProps {
  onAddColumn?: () => void;
  onReorderColumns?: (columns: string[]) => void;
  onUpdateColumn?: (oldName: string, newName?: string, defaultCategoryIds?: string[]) => void;
  onDeleteColumn?: (name: string) => void;
}

type ColumnEditState = { column: string; x: number; y: number } | null;

// ============================================
// Main Component
// ============================================

export const ColumnLabelsSimple = memo(function ColumnLabelsSimple({
  onAddColumn,
  onReorderColumns,
  onUpdateColumn,
  onDeleteColumn,
}: ColumnLabelsSimpleProps) {
  const columns = useColumnNames();
  const columnDefinitions = useColumnDefs();
  const columnLabelWidth = useSidebarWidth();
  const columnYOffsets = useColumnYOffsets();
  const rowAssignments = useRowAssignments();
  const rowHeight = useRowHeight();
  const totalHeight = useLayoutTotalHeight();
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [editingColumn, setEditingColumn] = useState<ColumnEditState>(null);

  // Subscribe to editColumnRequest from popupStore
  const editColumnRequest = useSelector(popupStore, s => s.context.editColumnRequest);
  const deleteColumnRequest = useSelector(popupStore, s => s.context.deleteColumnRequest);

  useEffect(() => {
    if (editColumnRequest && onUpdateColumn) {
      setEditingColumn(editColumnRequest);
      popupStore.send({ type: 'popup.clearEditColumnRequest' });
    }
  }, [editColumnRequest, onUpdateColumn]);

  useEffect(() => {
    if (deleteColumnRequest && onDeleteColumn) {
      onDeleteColumn(deleteColumnRequest.column);
      popupStore.send({ type: 'popup.clearDeleteColumnRequest' });
    }
  }, [deleteColumnRequest, onDeleteColumn]);

  const handleCloseEdit = useCallback(() => {
    setEditingColumn(null);
  }, []);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const columnId = event.active.id as string;
    setDraggedColumn(columnId);
  }, []);

  // Handle drag end - reorder columns
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedColumn(null);

    if (!over || active.id === over.id || !onReorderColumns) return;

    const oldIndex = columns.indexOf(active.id as string);
    const newIndex = columns.indexOf(over.id as string);

    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      const newColumns = [...columns];
      const [removed] = newColumns.splice(oldIndex, 1);
      newColumns.splice(newIndex, 0, removed);
      onReorderColumns(newColumns);
    }
  }, [columns, onReorderColumns]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div style={{ position: 'relative', height: totalHeight + 50 }}>
        {columns.map((column, index) => {
          const yOffset = columnYOffsets[column] ?? 0;
          const rowCount = Math.max(1, getMaxRow(rowAssignments[column]) + 1);
          return (
            <DraggableColumnLabel
              key={column}
              column={column}
              columnIndex={index}
              yOffset={yOffset}
              rowCount={rowCount}
              rowHeight={rowHeight}
              width={columnLabelWidth}
              isDragging={draggedColumn === column}
              canDrag={!!onReorderColumns}
              canEdit={!!onUpdateColumn}
            />
          );
        })}

        {/* Add Column Button */}
        {onAddColumn && (
          <div
            style={{
              position: 'absolute',
              top: totalHeight + 8,
              left: 0,
              width: columnLabelWidth,
              padding: '4px 8px',
            }}
          >
            <button
              onClick={onAddColumn}
              style={{
                width: '100%',
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                fontSize: 10,
                fontWeight: 500,
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                border: '1px dashed #d1d5db',
                borderRadius: 6,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.borderColor = '#9ca3af';
                e.currentTarget.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              <Plus size={14} />
              Neue Column
            </button>
          </div>
        )}
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {draggedColumn && (
          <div
            style={{
              padding: '4px 8px',
              backgroundColor: '#fff',
              border: '2px solid #3b82f6',
              borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              fontSize: 10,
              fontWeight: 400,
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <GripVertical size={14} style={{ color: '#9ca3af' }} />
            {draggedColumn}
          </div>
        )}
      </DragOverlay>

      {/* Column Edit Popover */}
      {editingColumn && onUpdateColumn && (() => {
        const colDef = columnDefinitions?.find(c => c.name === editingColumn.column)
          ?? { name: editingColumn.column, defaultCategoryIds: [] };
        return (
          <ColumnEditPopover
            columnDef={colDef}
            x={editingColumn.x}
            y={editingColumn.y}
            onSave={onUpdateColumn}
            onDelete={onDeleteColumn}
            onClose={handleCloseEdit}
          />
        );
      })()}
    </DndContext>
  );
});

// ============================================
// Draggable Column Label
// ============================================

interface DraggableColumnLabelProps {
  column: string;
  columnIndex: number;
  yOffset: number;
  rowCount: number;
  rowHeight: number;
  width: number;
  isDragging: boolean;
  canDrag: boolean;
  canEdit: boolean;
}

const DraggableColumnLabel = memo(function DraggableColumnLabel({
  column,
  columnIndex,
  yOffset,
  rowCount,
  rowHeight,
  width,
  isDragging,
  canDrag,
  canEdit,
}: DraggableColumnLabelProps) {
  const { t } = useTranslation();
  // Make column both draggable AND droppable
  const { attributes, listeners, setNodeRef: setDragRef } = useDraggable({
    id: column,
    disabled: !canDrag,
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: column,
  });

  // Combine both refs
  const setNodeRef = useCallback((node: HTMLDivElement | null) => {
    setDragRef(node);
    setDropRef(node);
  }, [setDragRef, setDropRef]);

  const height = rowCount * rowHeight + 8;

  const handleColumnNameClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    popupStore.send({ type: 'popup.openColumnMenu', column, x: e.clientX, y: e.clientY });
  }, [column]);

  return (
    <div
      ref={setNodeRef}
      onContextMenu={(e) => {
        e.preventDefault();
        popupStore.send({ type: 'popup.openColumnMenu', column, x: e.clientX, y: e.clientY });
      }}
      style={{
        position: 'absolute',
        top: yOffset,
        left: 0,
        width,
        height,
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        opacity: isDragging ? 0.4 : 1,
        backgroundColor: isDragging ? '#f3f4f6' : isOver ? '#e0f2fe' : columnIndex % 2 !== 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
        borderLeft: isOver ? '3px solid #3b82f6' : '3px solid transparent',
        transition: 'opacity 0.15s, background-color 0.15s, border-color 0.15s',
      }}
    >
      {/* Column name with drag handle */}
      <div
        style={{
          flex: 1,
          padding: '2px 4px 2px 2px',
          display: 'flex',
          alignItems: 'flex-start',
          overflow: 'hidden',
          gap: 1,
        }}
      >
        {/* Drag Handle */}
        {canDrag && (
          <button
            {...attributes}
            {...listeners}
            style={{
              padding: 0,
              marginTop: 2,
              cursor: 'grab',
              color: '#9ca3af',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#6b7280';
              e.currentTarget.style.backgroundColor = '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#9ca3af';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical size={10} />
          </button>
        )}

        <span
          style={{
            fontSize: 10,
            fontWeight: 400,
            color: '#1f2937',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            cursor: canEdit ? 'pointer' : 'default',
            borderRadius: 3,
            padding: '0 2px',
            transition: 'background-color 0.1s',
          }}
          title={canEdit ? t('boardv3.columnHeader.clickForOptions', { column }) : column}
          onClick={canEdit ? handleColumnNameClick : undefined}
          onMouseEnter={canEdit ? (e) => {
            e.currentTarget.style.backgroundColor = '#e0f2fe';
          } : undefined}
          onMouseLeave={canEdit ? (e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          } : undefined}
        >
          {column}
        </span>
      </div>
    </div>
  );
});

export default ColumnLabelsSimple;
